if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.mediaCount = 0;
        this.isTransitioning = false;
        this.transitionAnimation = 'crossfade'; // Default to crossfade
        
        this.elements = {
          container: null,
          scroller: null,
          dots: [],
          paddles: {
            previous: null,
            next: null
          },
          items: []
        };

        // Bound once on the host element: init() re-runs on every setImages(),
        // so listeners that target `this` must not live in bindEvents().
        this.addEventListener('keydown', this.handleKeyDown.bind(this));

        this.init();
      }

      init() {
        // Get elements
        this.elements.container = this.querySelector('.gallery-container');
        this.elements.scroller = this.querySelector('[id^="GalleryScroller"]');
        this.elements.dots = Array.from(this.querySelectorAll('.gallery-dot'));
        this.elements.paddles.previous = this.querySelector('.gallery-paddle-previous');
        this.elements.paddles.next = this.querySelector('.gallery-paddle-next');
        this.elements.items = Array.from(this.querySelectorAll('.gallery-item'));

        if (!this.elements.scroller) return;

        // Get initial state
        this.currentIndex = parseInt(this.elements.scroller.dataset.activeIndex) || 0;
        this.mediaCount = parseInt(this.elements.scroller.dataset.mediaCount) || 0;
        this.transitionAnimation = this.dataset.transitionAnimation || 'crossfade';
        
        // Bind event listeners
        this.bindEvents();
        
        // Initialize accessibility
        this.updateAccessibility();
      }

      bindEvents() {
        // Dot navigation
        this.elements.dots.forEach((dot, index) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToSlide(index);
          });
        });

        // Paddle navigation
        if (this.elements.paddles.previous) {
          this.elements.paddles.previous.addEventListener('click', (e) => {
            e.preventDefault();
            this.previousSlide();
          });
        }

        if (this.elements.paddles.next) {
          this.elements.paddles.next.addEventListener('click', (e) => {
            e.preventDefault();
            this.nextSlide();
          });
        }

        // Touch/swipe support
        this.addTouchSupport();

        this.elements.scroller.addEventListener('click', this.enlargeGallery.bind(this));
      }

      enlargeGallery() {
        if (this.dataset.allowEnlarge !== 'true') return;
        // Skip if a swipe just occurred (prevents accidental enlarge on mobile)
        if (this._lastSwipeTime && Date.now() - this._lastSwipeTime < 300) return;
        openEnlargeGallery(this, this.currentIndex);
      }

      addTouchSupport() {
        let startX = 0;
        let startY = 0;
        let direction = null; // 'horizontal' | 'vertical' | null
        const threshold = 50; // minimum distance for swipe
        const lockAngle = 10; // px moved before locking direction

        this.elements.scroller.addEventListener('touchstart', (e) => {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          direction = null;
        }, { passive: true });

        this.elements.scroller.addEventListener('touchmove', (e) => {
          if (direction === 'vertical') return; // let browser scroll

          const dx = Math.abs(e.touches[0].clientX - startX);
          const dy = Math.abs(e.touches[0].clientY - startY);

          if (!direction && (dx > lockAngle || dy > lockAngle)) {
            direction = dx > dy ? 'horizontal' : 'vertical';
          }

          if (direction === 'horizontal') {
            e.preventDefault();
          }
        }, { passive: false });

        this.elements.scroller.addEventListener('touchend', (e) => {
          if (direction !== 'horizontal') return;

          const deltaX = startX - e.changedTouches[0].clientX;
          if (Math.abs(deltaX) > threshold) {
            this._lastSwipeTime = Date.now();
            if (deltaX > 0) {
              this.nextSlide();
            } else {
              this.previousSlide();
            }
          }
        }, { passive: true });
      }

      handleKeyDown(e) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.previousSlide();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.nextSlide();
            break;
          case 'Home':
            e.preventDefault();
            this.goToSlide(0);
            break;
          case 'End':
            e.preventDefault();
            this.goToSlide(this.mediaCount - 1);
            break;
        }
      }

      goToSlide(index) {
        if (this.isTransitioning || index === this.currentIndex || index < 0 || index >= this.mediaCount) {
          return;
        }

        this.isTransitioning = true;
        const prevIndex = this.currentIndex;
        this.currentIndex = index;

        // Delegate to appropriate transition method
        if (this.transitionAnimation === 'slide') {
          this.transitionSlide(prevIndex, index);
        } else {
          this.transitionCrossfade(prevIndex, index);
        }

        // Update UI
        this.updateDots();
        this.updatePaddles();
        this.updateAccessibility();
      }

      // SLIDE TRANSITION - Horizontal transform animation
      transitionSlide(fromIndex, toIndex) {
        const shouldAnimate = Math.abs(toIndex - fromIndex) === 1;
        const translateX = -(toIndex * 100);
        
        // Set animation duration
        this.elements.scroller.style.animationDuration = shouldAnimate ? '300ms' : '0ms';
        
        // Apply transform
        this.elements.scroller.style.transform = `translateX(${translateX}%)`;

        // Reset transition flag after animation
        this._transitionTimer = setTimeout(() => {
          this.isTransitioning = false;
        }, shouldAnimate ? 300 : 0);
      }

      // CROSSFADE TRANSITION - Opacity-based stacked images
      transitionCrossfade(fromIndex, toIndex) {
        this.elements.items.forEach((item, itemIndex) => {
          if (itemIndex === toIndex) {
            // Show the target image
            item.classList.remove('opacity-0', 'pointer-events-none');
            item.classList.add('opacity-100');
            item.setAttribute('aria-hidden', 'false');
          } else {
            // Hide other images
            item.classList.remove('opacity-100');
            item.classList.add('opacity-0', 'pointer-events-none');
            item.setAttribute('aria-hidden', 'true');
          }
        });

        // Reset transition flag after crossfade completes
        this._transitionTimer = setTimeout(() => {
          this.isTransitioning = false;
        }, 300);
      }

      nextSlide() {
        if (this.currentIndex < this.mediaCount - 1) {
          this.goToSlide(this.currentIndex + 1);
        }
      }

      previousSlide() {
        if (this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1);
        }
      }

      updateDots() {
        this.elements.dots.forEach((dot, index) => {
          const isActive = index === this.currentIndex;
          dot.setAttribute('aria-selected', isActive.toString());
          dot.classList.toggle('bg-white', isActive);
          dot.classList.toggle('bg-white/50', !isActive);
        });
      }

      updatePaddles() {
        if (this.elements.paddles.previous) {
          this.elements.paddles.previous.disabled = this.currentIndex === 0;
          this.elements.paddles.previous.setAttribute('aria-hidden', this.currentIndex === 0 ? 'true' : 'false');
        }

        if (this.elements.paddles.next) {
          this.elements.paddles.next.disabled = this.currentIndex === this.mediaCount - 1;
          this.elements.paddles.next.setAttribute('aria-hidden', this.currentIndex === this.mediaCount - 1 ? 'true' : 'false');
        }
      }

      updateAccessibility() {
        this.elements.items.forEach((item, index) => {
          const isActive = index === this.currentIndex;
          item.setAttribute('aria-hidden', (!isActive).toString());
        });

        // Announce slide change for screen readers
        this.announceSlideChange();
      }

      announceSlideChange() {
        // Create or update live region for screen reader announcements
        let liveRegion = this.querySelector('.gallery-live-region');
        if (!liveRegion) {
          liveRegion = document.createElement('div');
          liveRegion.className = 'gallery-live-region sr-only';
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          this.appendChild(liveRegion);
        }

        const currentSlide = this.currentIndex + 1;
        liveRegion.textContent = `Image ${currentSlide} of ${this.mediaCount}`;
      }

      setActiveMedia(mediaId) {
        const mediaItem = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (mediaItem) {
          const index = parseInt(mediaItem.dataset.index);
          this.goToSlide(index);
        }
      }

      /**
       * Replace every slide with the ones in `sourceContainer` (a `[data-container]`
       * element, cloned) and show `startIndex`. The new slides are a fresh gallery:
       * a crossfade still running on the old slides must not block the first jump,
       * so the transition lock is cleared before the jump is made.
       */
      setImages(sourceContainer, startIndex = 0, { enlarge = false } = {}) {
        if (!sourceContainer) return;
        const clone = sourceContainer.cloneNode(true);

        if (enlarge) {
          // Normalize all gallery items for the enlarge dialog:
          // - Crossfade mode (absolute stacked)
          // - Images fill available space without cropping (object-contain)
          // - Consistent regardless of source (product gallery vs bento grid)
          clone.querySelectorAll('.gallery-item').forEach((item) => {
            item.className = `gallery-item absolute inset-0 flex size-full justify-center items-center transition-opacity duration-300 ${item.classList.contains('opacity-0') ? 'opacity-0 pointer-events-none' : 'opacity-100'}`;

            const figure = item.querySelector('figure');
            if (figure) {
              // Unwrap any enlarge-trigger buttons (not needed in dialog)
              const btn = figure.querySelector('[data-enlarge-trigger]');
              if (btn) {
                while (btn.firstChild) figure.insertBefore(btn.firstChild, btn);
                btn.remove();
              }
              // Fill the gallery item, let object-contain on the img handle aspect ratio
              figure.className = 'size-full';
              figure.style.cssText = '';
              const img = figure.querySelector('img');
              if (img) {
                img.className = 'block size-full object-contain';
                img.style.cssText = '';
              }
            }
          });

          // Build dot + paddle navigation for the dialog
          // (source may not have them, or they may have wrong styling)
          const items = clone.querySelectorAll('.gallery-item');
          const mediaCount = items.length;

          clone.querySelectorAll('.gallery-dotnav, .gallery-paddlenav').forEach(el => el.remove());

          if (mediaCount > 1) {
            const dotNav = document.createElement('div');
            dotNav.className = 'gallery-dotnav absolute bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 z-10';
            const ul = document.createElement('ul');
            ul.className = 'flex space-x-2';
            ul.setAttribute('role', 'tablist');
            for (let i = 0; i < mediaCount; i++) {
              const li = document.createElement('li');
              li.setAttribute('role', 'presentation');
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.setAttribute('role', 'tab');
              dot.setAttribute('aria-selected', i === startIndex ? 'true' : 'false');
              dot.setAttribute('aria-label', `Gallery item ${i + 1}`);
              dot.className = `gallery-dot w-2 h-2 rounded-full transition-all duration-200 ${i === startIndex ? 'bg-warm-900 dark:bg-white' : 'bg-warm-900/30 dark:bg-white/30 hover:bg-warm-900/60 dark:hover:bg-white/60'}`;
              dot.dataset.index = String(i);
              li.appendChild(dot);
              ul.appendChild(li);
            }
            dotNav.appendChild(ul);
            clone.appendChild(dotNav);

            const paddleNav = document.createElement('div');
            paddleNav.className = 'gallery-paddlenav';

            const prevBtn = document.createElement('button');
            prevBtn.type = 'button';
            prevBtn.className = 'hidden md:flex gallery-paddle gallery-paddle-previous absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-cream md:bg-cream/40 md:hover:bg-cream/80 rounded-[4.8px] items-center justify-center text-green-800 transition-opacity duration-200 opacity-0 group-hover/gallery:opacity-100 hover:opacity-100 focus-visible:opacity-100 pointer-events-none group-hover/gallery:pointer-events-auto';
            prevBtn.setAttribute('aria-label', 'Previous gallery image');
            prevBtn.dataset.direction = 'previous';
            prevBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            if (startIndex === 0) prevBtn.disabled = true;

            const nextBtn = document.createElement('button');
            nextBtn.type = 'button';
            nextBtn.className = 'hidden md:flex gallery-paddle gallery-paddle-next absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-cream md:bg-cream/40 md:hover:bg-cream/80 rounded-[4.8px] items-center justify-center text-green-800 transition-opacity duration-200 opacity-0 group-hover/gallery:opacity-100 hover:opacity-100 focus-visible:opacity-100 pointer-events-none group-hover/gallery:pointer-events-auto';
            nextBtn.setAttribute('aria-label', 'Next gallery image');
            nextBtn.dataset.direction = 'next';
            nextBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            if (startIndex === mediaCount - 1) nextBtn.disabled = true;

            paddleNav.appendChild(prevBtn);
            paddleNav.appendChild(nextBtn);
            clone.appendChild(paddleNav);
          }
        }

        // Replace the existing container in this gallery
        const oldContainer = this.elements.container;
        if (oldContainer) {
          oldContainer.replaceWith(clone);
        } else {
          // No gallery-container exists (e.g. "No image" placeholder) — clear first
          this.replaceChildren(clone);
        }

        // Re-init gallery with new DOM
        clearTimeout(this._transitionTimer);
        this.isTransitioning = false;
        this.init();
        if (startIndex !== 0) {
          this.goToSlide(startIndex);
        }
      }

      // Auto-play functionality (optional)
      startAutoPlay(interval = 5000) {
        this.stopAutoPlay();
        this.autoPlayInterval = setInterval(() => {
          if (this.currentIndex === this.mediaCount - 1) {
            this.goToSlide(0);
          } else {
            this.nextSlide(); 
          }
        }, interval);
      }

      stopAutoPlay() {
        if (this.autoPlayInterval) {
          clearInterval(this.autoPlayInterval);
          this.autoPlayInterval = null;
        }
      }

      // Pause auto-play on hover
      connectedCallback() {
        this.addEventListener('mouseenter', () => this.stopAutoPlay());
        this.addEventListener('mouseleave', () => {
          if (this.dataset.autoPlay === 'true') {
            this.startAutoPlay();
          }
        });
      }

      disconnectedCallback() {
        this.stopAutoPlay();
      }
    }
  );
}
